import { Alert } from '@creditornot/cb-alert';
import { Spinner } from '@creditornot/cb-progress';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Redirect } from 'react-router';
import styled from 'styled-components';
import { createEnumParam, StringParam, useQueryParams } from 'use-query-params';

import { getCampaign } from 'apis/restaurant-api/merchant-admin/campaigns/api';
import { CampaignType } from 'apis/restaurant-api/merchant-admin/campaigns/types';
import { ProductLine, Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { convertMonetaryIntToDecimal } from 'common/utils/currencyDecimals';
import CampaignHeader from 'modules/campaigns/components/CampaignHeader';
import ItemDiscountForm from 'modules/campaigns/components/forms/ItemDiscountForm/ItemDiscountForm';
import { useCampaignModuleContext } from 'modules/campaigns/context';
import { useAdCampaignMutations } from 'modules/campaigns/hooks/useAdCampaignMutations';
import { useAdCampaignVenueConfiguration } from 'modules/campaigns/hooks/useAdCampaignVenueConfiguration';
import { useIsMultiItemCampaignSupported } from 'modules/campaigns/hooks/useIsMultiItemCampaignSupported';
import { getCommonCampaignFormValues } from 'modules/campaigns/utils/getCommonCampaignFormValues';

import { CampaignsQueryKeys } from '../../campaignsQueryKeys';
import CampaignTypeSelector from '../../components/CampaignTypeSelector/CampaignTypeSelector';
import { API_UNLIMITED_MAX_DELIVERY_DISTANCE } from '../../components/budget-section/constants';
import { AdCampaignForm } from '../../components/forms/AdCampaignForm/AdCampaignForm';
import BasketDiscountForm from '../../components/forms/BasketDiscountForm/BasketDiscountForm';
import DeliveryDiscountForm from '../../components/forms/DeliveryDiscountForm/DeliveryDiscountForm';
import EndOfDayDiscountForm from '../../components/forms/ItemDiscountForm/EndOfDayDiscountForm';
import { useCampaignConfiguration } from '../../hooks/useCampaignConfiguration';
import { useCampaignMutations } from '../../hooks/useCampaignMutations';
import useCampaignsOptions from '../../hooks/useCampaignsOptions';
import { useCampaignsRoutes } from '../../hooks/useCampaignsRoutes';
import {
  AdCampaignFormValues,
  BasketDiscountFormValues,
  CampaignsFormValues,
  CommonCampaignFormValues,
  DeliveryDiscountFormValues,
  EndOfDayFormValues,
  ItemDiscountFormValues,
  ItemDiscountType,
  VenuesValidationError,
} from '../../types';
import { getBasketDiscountType } from '../../utils/getBasketDiscountType';
import { getBasketDiscountValue } from '../../utils/getBasketDiscountValue';
import { getItemDiscountType } from '../../utils/getItemDiscountType';
import { useTelemetryNewCampaignView } from '../../utils/telemetry/telemetry';

export type NewCampaignPageProps = {
  merchantId: string;
  venueIds?: string[];
  venueDetails?: Pick<Venue, 'name' | 'short_description' | 'listimage'>;
  campaignTypesPermittedToEdit: CampaignType[];
  timezone: string;
  menuId?: string;
  country: Country;
  productLines: ProductLine[];
};

const SpinnerContainer = styled.div`
  display: flex;
  flex: 1 1 auto;
  justify-content: center;
  padding-block-start: 32px;
`;

const StyledWrapper = styled.div`
  width: 100%;
  margin: 0 auto;
`;

const StyledAlert = styled(Alert)`
  margin-block-start: 32px;
  margin-inline: 16px;
`;

export const NewCampaignPage = ({
  merchantId,
  venueIds = [],
  venueDetails,
  campaignTypesPermittedToEdit,
  menuId,
  timezone,
  country,
  productLines,
}: NewCampaignPageProps) => {
  const { currency } = useCampaignModuleContext();
  const [queryParams, setQueryParams] = useQueryParams({
    campaignType: createEnumParam([
      CampaignType.DELIVERY_DISCOUNT,
      CampaignType.BASKET_DISCOUNT,
      CampaignType.ADS,
      CampaignType.ITEM_DISCOUNT,
      CampaignType.END_OF_DAY,
    ]),
    campaignId: StringParam,
  });

  const campaignsRoutes = useCampaignsRoutes();

  const { getCampaignTitleByType } = useCampaignsOptions();

  const [venuesValidationErrors, setVenuesValidationErrors] = useState<VenuesValidationError | undefined>();

  const { create } = useCampaignMutations({
    merchantId,
    venueIds,
    onInvalidVenuesError: errorProps => {
      setVenuesValidationErrors(errorProps);
    },
  });
  const { create: createAdCampaign } = useAdCampaignMutations({ venueIds });

  const isAdCampaign = queryParams.campaignType === CampaignType.ADS;

  const isCampaignQueryEnabled = !isAdCampaign && !!merchantId && !!queryParams.campaignId;
  const {
    configurationQuery: { data: configuration, isLoading: isConfigurationLoading },
    campaignTypesAvailableToEdit,
  } = useCampaignConfiguration({ merchantId, venueIds, campaignTypesPermittedToEdit });

  const { data: campaign, isLoading: isCampaignLoading } = useQuery(
    CampaignsQueryKeys.GET_SINGLE(merchantId, queryParams.campaignId || ''),
    () =>
      getCampaign(merchantId, queryParams.campaignId || '').then(campaign => ({
        ...campaign,
        campaign_type: campaign.is_end_of_day_campaign ? CampaignType.END_OF_DAY : campaign.campaign_type,
      })),
    {
      refetchOnWindowFocus: false,
      enabled: isCampaignQueryEnabled,
      retry: false,
      onError: () => {
        setQueryParams(prevState => ({ ...prevState, campaignId: undefined }));
      },
    },
  );

  const { data: venueAdsConfig, isLoading: isVenueAdConfigLoading } = useAdCampaignVenueConfiguration({
    venue: venueIds[0],
    country,
    enabled: !isCampaignQueryEnabled,
  });

  const { isMultiItemCampaignSupported } = useIsMultiItemCampaignSupported();

  const defaultValues = useMemo<CampaignsFormValues | AdCampaignFormValues | undefined>(() => {
    if (!configuration) {
      return;
    }

    const campaignType = campaign?.campaign_type || queryParams.campaignType;

    const commonValues: CommonCampaignFormValues = getCommonCampaignFormValues({
      campaign,
      configuration,
      currency,
      country,
      timezone,
      campaignType,
      venueIds,
    });

    if (campaignType === CampaignType.DELIVERY_DISCOUNT) {
      const deliveryDiscountDefaultValues: DeliveryDiscountFormValues = {
        campaignType,
        maxDeliveryDistance:
          campaign?.max_delivery_distance ||
          configuration.max_delivery_distances[1] ||
          configuration.max_delivery_distances[0],
        ...commonValues,
      };

      return deliveryDiscountDefaultValues;
    }

    if (campaignType === CampaignType.BASKET_DISCOUNT) {
      const basketDiscountConfig =
        configuration.basket_discounts[
          campaign?.min_basket_size || Number(Object.keys(configuration.basket_discounts)[0])
        ];
      const discountType = getBasketDiscountType(campaign?.basket_discount);

      const basketDiscountDefaultValues: BasketDiscountFormValues = {
        campaignType,
        discountType,
        discountValue: getBasketDiscountValue(discountType, {
          amount: campaign?.basket_discount.amount || basketDiscountConfig.recommended_amount,
          percentage: campaign?.basket_discount.percentage || basketDiscountConfig.recommended_percentage,
          upperLimit: campaign?.basket_discount.upper_limit || basketDiscountConfig.upper_limits[0],
        }),
        maxDeliveryDistance: API_UNLIMITED_MAX_DELIVERY_DISTANCE,
        ...commonValues,
      };

      return basketDiscountDefaultValues;
    }

    if (campaignType === CampaignType.ITEM_DISCOUNT || campaignType === CampaignType.END_OF_DAY) {
      const itemDiscountDefaultValues: ItemDiscountFormValues | EndOfDayFormValues = {
        campaignType,
        discountType: campaign?.item_discount
          ? getItemDiscountType(campaign.item_discount)
          : ItemDiscountType.X_FOR_FREE,
        amount: convertMonetaryIntToDecimal(campaign?.item_discount?.amount ?? 0, currency),
        itemIds: campaign?.item_discount?.target_item?.item_id ? [campaign.item_discount.target_item.item_id] : [],
        itemTriggerCount: campaign?.item_discount?.item_trigger_count ?? undefined,
        percentage: campaign?.item_discount?.percentage ?? undefined,
        freeItemsCount: campaign?.item_discount?.free_items_count ?? undefined,
        maxDeliveryDistance: API_UNLIMITED_MAX_DELIVERY_DISTANCE,
        ...commonValues,
      };

      if (campaignType === CampaignType.END_OF_DAY) {
        return {
          ...itemDiscountDefaultValues,
          startDate: DateTime.now().setZone(timezone).toISO(),
          endDate: DateTime.now().setZone(timezone).endOf('day').toISO(),
          discountType: ItemDiscountType.PERCENTAGE_OFF,
        };
      }

      return itemDiscountDefaultValues;
    }

    if (campaignType === CampaignType.ADS && venueAdsConfig) {
      return {
        campaignType,
        budgetAmount: venueAdsConfig?.budgetOptions.find(o => o.type === 'recommended')?.value,
        startDate: DateTime.local().toISO(),
        budgetType: 'recommended',
        bidStrategy: 'auto',
        scheduleStrategy: 'alwaysOn',
      } as AdCampaignFormValues;
    }

    return;
  }, [configuration, campaign, queryParams.campaignType, currency, country, timezone, venueIds, venueAdsConfig]);

  const handleSubmit = (values: CampaignsFormValues) => {
    if (!configuration) {
      return;
    }

    if (values.campaignType === CampaignType.DELIVERY_DISCOUNT) {
      const minBasketSize = values.minBasketSize || Number(Object.keys(configuration.delivery_discounts)[0]);
      const deliveryDiscountConfig = configuration.delivery_discounts[minBasketSize];

      return create.mutate({
        ...values,
        currency,
        deliveryDiscount: {
          amount: deliveryDiscountConfig.recommended_amount,
          percentage: deliveryDiscountConfig.recommended_percentage,
        },
      });
    }

    if (values.campaignType === CampaignType.ITEM_DISCOUNT) {
      return create.mutate({ ...values, currency, isMultiItemCampaignSupported });
    }

    if (values.campaignType === CampaignType.END_OF_DAY) {
      return create.mutate({ ...values, currency, isMultiItemCampaignSupported: false });
    }

    return create.mutate({ ...values, currency });
  };

  const isLoading =
    (isCampaignQueryEnabled && isCampaignLoading) ||
    (!!merchantId && isConfigurationLoading) ||
    (isAdCampaign && isVenueAdConfigLoading);

  const isCampaignTypeAvailable =
    configuration && defaultValues?.campaignType && campaignTypesAvailableToEdit.includes(defaultValues.campaignType);

  useTelemetryNewCampaignView(defaultValues?.campaignType, !!isCampaignTypeAvailable);

  const handleSubmitAdCampaign = (values: AdCampaignFormValues) =>
    createAdCampaign.mutate({ ...values, country, currency });

  if (campaign?.created_by_wolt) {
    return <Redirect to={campaignsRoutes.index} />;
  }

  return (
    <>
      <CampaignHeader
        title={isCampaignTypeAvailable ? getCampaignTitleByType(defaultValues.campaignType) : ''}
        isCampaignSubpage
      />
      <StyledWrapper>
        {isLoading ? (
          <SpinnerContainer data-testid="spinner">
            <Spinner size="large" />
          </SpinnerContainer>
        ) : isCampaignTypeAvailable ? (
          <>
            {defaultValues.campaignType === CampaignType.DELIVERY_DISCOUNT && (
              <DeliveryDiscountForm
                venueDetails={venueDetails}
                defaultValues={defaultValues}
                configuration={configuration}
                timezone={timezone}
                country={country}
                isLoading={create.isLoading}
                venuesValidationErrors={venuesValidationErrors}
                onSubmit={handleSubmit}
              />
            )}
            {defaultValues.campaignType === CampaignType.BASKET_DISCOUNT && (
              <BasketDiscountForm
                venueDetails={venueDetails}
                defaultValues={defaultValues}
                configuration={configuration}
                timezone={timezone}
                country={country}
                isLoading={create.isLoading}
                venuesValidationErrors={venuesValidationErrors}
                onSubmit={handleSubmit}
              />
            )}
            {defaultValues.campaignType === CampaignType.ITEM_DISCOUNT &&
              (menuId ? (
                <ItemDiscountForm
                  venueDetails={venueDetails}
                  defaultValues={defaultValues}
                  configuration={configuration}
                  productLines={productLines}
                  timezone={timezone}
                  country={country}
                  isLoading={create.isLoading}
                  menuId={menuId}
                  singleItemOnly={!isMultiItemCampaignSupported}
                  onSubmit={handleSubmit}
                />
              ) : (
                <StyledAlert variant="error">
                  <FormattedMessage id="campaigns.menu-unavailable" />
                </StyledAlert>
              ))}
            {defaultValues.campaignType === CampaignType.END_OF_DAY &&
              (menuId ? (
                <EndOfDayDiscountForm
                  venueDetails={venueDetails}
                  defaultValues={defaultValues}
                  configuration={configuration}
                  productLines={productLines}
                  timezone={timezone}
                  country={country}
                  isLoading={create.isLoading}
                  menuId={menuId}
                  singleItemOnly
                  onSubmit={handleSubmit}
                />
              ) : (
                <StyledAlert variant="error">
                  <FormattedMessage id="campaigns.menu-unavailable" />
                </StyledAlert>
              ))}
            {defaultValues.campaignType === CampaignType.ADS && venueAdsConfig && (
              <AdCampaignForm
                venueAdsConfig={venueAdsConfig}
                initialValues={defaultValues}
                onSubmit={handleSubmitAdCampaign}
                isLoading={createAdCampaign.isLoading || isLoading}
                venueDetails={venueDetails}
              />
            )}
          </>
        ) : (
          <CampaignTypeSelector
            onNewCampaign={campaignType => setQueryParams({ campaignType })}
            campaignTypesAvailableToEdit={campaignTypesAvailableToEdit}
          />
        )}
      </StyledWrapper>
    </>
  );
};
